import React, { useState, useEffect } from 'react';
import axios from 'axios';

// FDI Teeth numbering system (India standard)
const TEETH_GROUPS = [
  { label: 'Upper Right (UR)', teeth: ['18','17','16','15','14','13','12','11'] },
  { label: 'Upper Left (UL)',  teeth: ['21','22','23','24','25','26','27','28'] },
  { label: 'Lower Left (LL)',  teeth: ['31','32','33','34','35','36','37','38'] },
  { label: 'Lower Right (LR)', teeth: ['41','42','43','44','45','46','47','48'] },
];
const ALL_TEETH = TEETH_GROUPS.flatMap(g => g.teeth);

const SURFACES = [
  { code: 'M', label: 'Mesial' },
  { code: 'D', label: 'Distal' },
  { code: 'B', label: 'Buccal' },
  { code: 'L', label: 'Lingual' },
  { code: 'O', label: 'Occlusal' },
  { code: 'I', label: 'Incisal' },
  { code: 'P', label: 'Palatal' },
];

const BLANK_TOOTH = { toothNumber: '', surfaces: [], painOnPalpation: null, top: null, notes: '' };

const BLANK_FORM = {
  clinicalNotes: [],
  chiefComplaint: '',
  findings: '',
  investigation: '',
  diagnosis: '',
  medicines: [],
  notes: '',
};

const BLANK_MED = { name: '', dosage: '', duration: '', instructions: '' };

const inputStyle = { padding: '9px 12px', borderRadius: '6px', border: '1px solid #dee2e6', fontSize: '0.92rem', width: '100%', boxSizing: 'border-box' };
const labelStyle = { display: 'block', marginBottom: '6px', fontSize: '0.82rem', color: '#555', fontWeight: 'bold' };
const secHead = { color: '#003554', margin: '0 0 14px', fontSize: '1rem', fontWeight: 'bold', borderBottom: '2px solid #e9ecef', paddingBottom: '8px' };

// Preset side panel fields
const PRESET_FIELDS = [
  { key: 'chiefComplaint', label: '📋 Chief Complaint' },
  { key: 'findings',       label: '🔍 Findings' },
  { key: 'investigation',  label: '🧪 Investigation' },
  { key: 'diagnosis',      label: '🩺 Diagnosis' },
];

export default function PrescriptionForm({ patientId, API_BASE, onSaved, onCancel, presets }) {
  const [form, setForm] = useState(BLANK_FORM);
  const [activeToothIdx, setActiveToothIdx] = useState(null);
  const [newTooth, setNewTooth] = useState(BLANK_TOOTH);
  const [newMed, setNewMed] = useState(BLANK_MED);
  const [activePresetKey, setActivePresetKey] = useState('chiefComplaint');
  const [saving, setSaving] = useState(false);

  // Drug safety (Veeransh CDS)
  const [safety, setSafety] = useState(null);          // { severe, warning, info, summary }
  const [safetyBusy, setSafetyBusy] = useState(false);
  const [showOverride, setShowOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');

  // Live safety check — debounced 500ms after any medicine list edit.
  // Runs even with zero medicines so the panel clears correctly.
  useEffect(() => {
    const meds = form.medicines || [];
    const t = setTimeout(async () => {
      if (meds.length === 0) { setSafety(null); return; }
      setSafetyBusy(true);
      try {
        const res = await axios.post(`${API_BASE}/prescriptions/safety-check`, {
          medicines: meds, patientId,
        });
        setSafety(res.data);
      } catch { setSafety(null); }
      setSafetyBusy(false);
    }, 500);
    return () => clearTimeout(t);
  }, [form.medicines, patientId, API_BASE]);

  // presets = { chiefComplaint: [...], findings: [...], investigation: [...], diagnosis: [...] }

  const doSave = async (overrideReasonValue = '') => {
    setSaving(true);
    try {
      const payload = { ...form, patientId };
      if (overrideReasonValue) payload.safetyOverrideReason = overrideReasonValue;
      await axios.post(`${API_BASE}/prescriptions/add`, payload);
      alert('Parcha Save Ho Gaya!');
      onSaved();
    } catch (e) {
      const status = e.response?.status;
      const data = e.response?.data;
      if (status === 409 && data?.safety?.severe?.length) {
        // Server enforced block even though our client check may have allowed
        // — surface the modal with the server's authoritative list.
        setSafety(data.safety);
        setShowOverride(true);
      } else {
        alert('Save karne mein error: ' + (data?.error || e.message));
      }
    }
    setSaving(false);
  };

  const handleSave = () => {
    if (safety?.severe?.length) {
      setShowOverride(true);
      return;
    }
    doSave();
  };

  const confirmOverride = () => {
    const reason = overrideReason.trim();
    if (!reason) { alert('Clinical justification likhna zaruri hai.'); return; }
    setShowOverride(false);
    doSave(reason);
    setOverrideReason('');
  };

  // Append preset to a field
  const appendPreset = (fieldKey, label) => {
    setForm(f => ({
      ...f,
      [fieldKey]: f[fieldKey] ? f[fieldKey] + ', ' + label : label
    }));
  };

  // Clinical notes - add tooth
  const addTooth = () => {
    if (!newTooth.toothNumber) { alert('Tooth number is required!'); return; }
    setForm(f => ({ ...f, clinicalNotes: [...f.clinicalNotes, { ...newTooth }] }));
    setNewTooth(BLANK_TOOTH);
  };

  const removeTooth = (idx) => {
    setForm(f => ({ ...f, clinicalNotes: f.clinicalNotes.filter((_, i) => i !== idx) }));
  };

  const updateTooth = (idx, field, val) => {
    setForm(f => {
      const notes = [...f.clinicalNotes];
      notes[idx] = { ...notes[idx], [field]: val };
      return { ...f, clinicalNotes: notes };
    });
  };

  const toggleSurface = (surfaces, code) =>
    surfaces.includes(code) ? surfaces.filter(s => s !== code) : [...surfaces, code];

  // Add medicine
  const addMed = () => {
    if (!newMed.name) { alert('Medicine name is required!'); return; }
    setForm(f => ({ ...f, medicines: [...f.medicines, { ...newMed }] }));
    setNewMed(BLANK_MED);
  };

  const removeMed = (idx) => {
    setForm(f => ({ ...f, medicines: f.medicines.filter((_, i) => i !== idx) }));
  };

  const yesNoBtn = (current, val, onChange) => (
    <button onClick={() => onChange(current === val ? null : val)}
      style={{ padding: '5px 14px', borderRadius: '6px', border: '2px solid',
        borderColor: current === val ? (val === 'Yes' ? '#28a745' : '#dc3545') : '#dee2e6',
        backgroundColor: current === val ? (val === 'Yes' ? '#28a745' : '#dc3545') : 'white',
        color: current === val ? 'white' : '#555', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.82rem' }}>
      {val}
    </button>
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: '0', minHeight: '600px' }}>

      {/* ── LEFT: FORM ── */}
      <div style={{ paddingRight: '20px', overflowY: 'auto', maxHeight: '80vh' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, color: '#003554', fontSize: '1.1rem' }}>📋 New Prescription</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={onCancel}
              style={{ padding: '8px 18px', borderRadius: '7px', border: '1px solid #dee2e6', background: 'white', cursor: 'pointer', color: '#555' }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '8px 22px', borderRadius: '7px', border: 'none', backgroundColor: '#006494', color: 'white', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}>
              {saving ? 'Saving...' : 'Save Parcha'}
            </button>
          </div>
        </div>

        {/* ── SECTION 1: Clinical Notes (Per Tooth) ── */}
        <div style={{ backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '16px', marginBottom: '18px' }}>
          <p style={secHead}>🦷 Clinical Notes (Teeth)</p>

          {/* Saved tooth entries */}
          {form.clinicalNotes.map((cn, idx) => (
            <div key={idx} style={{ backgroundColor: 'white', borderRadius: '8px', padding: '12px', marginBottom: '10px', border: '1px solid #e9ecef', position: 'relative' }}>
              <button onClick={() => removeTooth(idx)}
                style={{ position: 'absolute', top: '8px', right: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', fontWeight: 'bold', fontSize: '1rem' }}>✕</button>

              <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr 1fr', gap: '10px', alignItems: 'start' }}>
                <div>
                  <div style={{ fontWeight: 'bold', color: '#003554', fontSize: '1.1rem', textAlign: 'center',
                    backgroundColor: '#e8f4fd', borderRadius: '6px', padding: '6px' }}>
                    #{cn.toothNumber}
                  </div>
                </div>

                <div>
                  <label style={{ ...labelStyle, fontSize: '0.75rem' }}>Surfaces</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {SURFACES.map(s => (
                      <button key={s.code}
                        onClick={() => updateTooth(idx, 'surfaces', toggleSurface(cn.surfaces, s.code))}
                        style={{ padding: '3px 8px', borderRadius: '4px', border: '1px solid',
                          borderColor: cn.surfaces.includes(s.code) ? '#006494' : '#dee2e6',
                          backgroundColor: cn.surfaces.includes(s.code) ? '#006494' : 'white',
                          color: cn.surfaces.includes(s.code) ? 'white' : '#555',
                          cursor: 'pointer', fontSize: '0.75rem', fontWeight: 'bold' }}>
                        {s.code}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={{ ...labelStyle, fontSize: '0.75rem' }}>Pain on Palpation</label>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {yesNoBtn(cn.painOnPalpation, 'Yes', val => updateTooth(idx, 'painOnPalpation', val))}
                    {yesNoBtn(cn.painOnPalpation, 'No', val => updateTooth(idx, 'painOnPalpation', val))}
                  </div>
                </div>

                <div>
                  <label style={{ ...labelStyle, fontSize: '0.75rem' }}>TOP</label>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {yesNoBtn(cn.top, 'Yes', val => updateTooth(idx, 'top', val))}
                    {yesNoBtn(cn.top, 'No', val => updateTooth(idx, 'top', val))}
                  </div>
                </div>
              </div>

              {/* Per-tooth notes */}
              <div style={{ marginTop: '8px' }}>
                <input placeholder="Additional notes for this tooth..."
                  value={cn.notes}
                  onChange={e => updateTooth(idx, 'notes', e.target.value)}
                  style={{ ...inputStyle, fontSize: '0.82rem', backgroundColor: '#fafafa' }} />
              </div>
            </div>
          ))}

          {/* Add new tooth entry */}
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '12px', border: '2px dashed #dee2e6' }}>
            <p style={{ margin: '0 0 10px', fontSize: '0.82rem', color: '#888', fontWeight: 'bold' }}>+ Tooth Add Karo</p>
            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr 1fr 1fr auto', gap: '10px', alignItems: 'end' }}>

              {/* Tooth selector */}
              <div>
                <label style={{ ...labelStyle, fontSize: '0.75rem' }}>Tooth Number</label>
                <select value={newTooth.toothNumber}
                  onChange={e => setNewTooth({ ...newTooth, toothNumber: e.target.value })}
                  style={inputStyle}>
                  <option value="">Select Tooth</option>
                  {TEETH_GROUPS.map(g => (
                    <optgroup key={g.label} label={g.label}>
                      {g.teeth.map(t => <option key={t} value={t}>#{t}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>

              {/* Surfaces */}
              <div>
                <label style={{ ...labelStyle, fontSize: '0.75rem' }}>Surfaces</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {SURFACES.map(s => (
                    <button key={s.code}
                      onClick={() => setNewTooth(t => ({ ...t, surfaces: toggleSurface(t.surfaces, s.code) }))}
                      title={s.label}
                      style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid',
                        borderColor: newTooth.surfaces.includes(s.code) ? '#006494' : '#dee2e6',
                        backgroundColor: newTooth.surfaces.includes(s.code) ? '#006494' : 'white',
                        color: newTooth.surfaces.includes(s.code) ? 'white' : '#555',
                        cursor: 'pointer', fontSize: '0.75rem', fontWeight: 'bold' }}>
                      {s.code}
                    </button>
                  ))}
                </div>
              </div>

              {/* Pain on Palpation */}
              <div>
                <label style={{ ...labelStyle, fontSize: '0.75rem' }}>Pain on Palpation</label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {yesNoBtn(newTooth.painOnPalpation, 'Yes', val => setNewTooth(t => ({ ...t, painOnPalpation: val })))}
                  {yesNoBtn(newTooth.painOnPalpation, 'No', val => setNewTooth(t => ({ ...t, painOnPalpation: val })))}
                </div>
              </div>

              {/* TOP */}
              <div>
                <label style={{ ...labelStyle, fontSize: '0.75rem' }}>TOP</label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {yesNoBtn(newTooth.top, 'Yes', val => setNewTooth(t => ({ ...t, top: val })))}
                  {yesNoBtn(newTooth.top, 'No', val => setNewTooth(t => ({ ...t, top: val })))}
                </div>
              </div>

              {/* Add button */}
              <button onClick={addTooth}
                style={{ padding: '9px 18px', backgroundColor: '#006494', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                + Add
              </button>
            </div>
          </div>
        </div>

        {/* ── SECTION 2: Chief Complaint, Findings, Investigation, Diagnosis ── */}
        {PRESET_FIELDS.map(field => (
          <div key={field.key} style={{ marginBottom: '16px' }}>
            <label style={{ ...labelStyle, fontSize: '0.9rem' }}>{field.label}</label>
            <textarea
              rows={3}
              placeholder={`${field.label.replace(/^.+ /,'')} type here...`}
              value={form[field.key]}
              onFocus={() => setActivePresetKey(field.key)}
              onChange={e => setForm(f => ({ ...f, [field.key]: e.target.value }))}
              style={{ ...inputStyle, resize: 'vertical', minHeight: '72px' }}
            />
          </div>
        ))}

        {/* ── SECTION 3: Medicines ── */}
        <div style={{ backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '16px', marginBottom: '18px' }}>
          <p style={secHead}>💊 Medicines</p>

          {/* Live safety panel — Veeransh Clinical Decision Support */}
          {form.medicines.length > 0 && (
            <div style={{
              marginBottom: '12px',
              padding: '10px 12px',
              borderRadius: '8px',
              border: `2px solid ${
                safety?.severe?.length ? '#dc3545'
                : safety?.warning?.length ? '#ffc107'
                : safety ? '#28a745' : '#dee2e6'
              }`,
              backgroundColor: safety?.severe?.length ? '#fff5f5'
                : safety?.warning?.length ? '#fffbea'
                : safety ? '#f4fff5' : '#fafafa',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <span style={{ fontSize: '0.88rem', fontWeight: 'bold',
                  color: safety?.severe?.length ? '#c0392b'
                    : safety?.warning?.length ? '#b8860b'
                    : safety ? '#27ae60' : '#666',
                }}>
                  🛡️ Veeransh Safety Check
                </span>
                {safetyBusy && <span style={{ fontSize: '0.75rem', color: '#888' }}>checking…</span>}
                {!safetyBusy && safety && (
                  <span style={{ fontSize: '0.75rem', color: '#555', marginLeft: 'auto' }}>
                    {safety.severe.length} severe · {safety.warning.length} warning · {safety.info.length} info
                    {!safety.summary.patientContextUsed && ' · no patient ctx'}
                  </span>
                )}
              </div>
              {!safetyBusy && safety && safety.severe.length === 0 && safety.warning.length === 0 && safety.info.length === 0 && (
                <div style={{ fontSize: '0.82rem', color: '#27ae60' }}>✓ Koi issue nahi — safe to prescribe.</div>
              )}
              {!safetyBusy && safety && (safety.severe.length > 0 || safety.warning.length > 0 || safety.info.length > 0) && (
                <div style={{ display: 'grid', gap: '5px', marginTop: '4px' }}>
                  {safety.severe.map((s, i) => (
                    <div key={`sev-${i}`} style={{ fontSize: '0.82rem', padding: '5px 8px', background: '#ffe0e0', borderRadius: '5px', borderLeft: '3px solid #dc3545' }}>
                      <strong style={{ color: '#c0392b' }}>⛔ {s.drug}:</strong> {s.message}
                      {s.condition && <span style={{ color: '#888', marginLeft: '4px' }}>(condition: {s.condition})</span>}
                    </div>
                  ))}
                  {safety.warning.map((w, i) => (
                    <div key={`warn-${i}`} style={{ fontSize: '0.82rem', padding: '5px 8px', background: '#fff4cc', borderRadius: '5px', borderLeft: '3px solid #ffc107' }}>
                      <strong style={{ color: '#b8860b' }}>⚠️ {w.drug}:</strong> {w.message}
                    </div>
                  ))}
                  {safety.info.map((r, i) => (
                    <div key={`info-${i}`} style={{ fontSize: '0.8rem', padding: '4px 8px', background: '#e8f4fd', borderRadius: '5px', borderLeft: '3px solid #006494', color: '#555' }}>
                      <strong>ℹ️ {r.drug}:</strong> {r.message}
                    </div>
                  ))}
                </div>
              )}
              {!safety?.summary?.patientContextUsed && (
                <div style={{ fontSize: '0.72rem', color: '#999', marginTop: '5px' }}>
                  💡 Patient allergies/history checks need patientId — this open form may lack it.
                </div>
              )}
            </div>
          )}

          {/* Saved medicines */}
          {form.medicines.map((m, idx) => (
            <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px', backgroundColor: 'white', padding: '8px 12px', borderRadius: '7px', border: '1px solid #e9ecef' }}>
              <div style={{ flex: 1 }}>
                <strong style={{ color: '#003554', fontSize: '0.9rem' }}>{m.name}</strong>
                <span style={{ color: '#888', fontSize: '0.82rem', marginLeft: '10px' }}>
                  {[m.dosage, m.duration, m.instructions].filter(Boolean).join(' • ')}
                </span>
              </div>
              <button onClick={() => removeMed(idx)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', fontWeight: 'bold', fontSize: '1rem' }}>✕</button>
            </div>
          ))}

          {/* Add medicine row */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr auto', gap: '8px', alignItems: 'end' }}>
            <div>
              <label style={{ ...labelStyle, fontSize: '0.75rem' }}>Medicine Name *</label>
              <input placeholder="e.g. Amoxicillin 500mg" value={newMed.name}
                onChange={e => setNewMed({ ...newMed, name: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={{ ...labelStyle, fontSize: '0.75rem' }}>Dosage</label>
              <input placeholder="e.g. 1-0-1" value={newMed.dosage}
                onChange={e => setNewMed({ ...newMed, dosage: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={{ ...labelStyle, fontSize: '0.75rem' }}>Duration</label>
              <input placeholder="e.g. 5 days" value={newMed.duration}
                onChange={e => setNewMed({ ...newMed, duration: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={{ ...labelStyle, fontSize: '0.75rem' }}>Instructions</label>
              <input placeholder="e.g. After meal" value={newMed.instructions}
                onChange={e => setNewMed({ ...newMed, instructions: e.target.value })} style={inputStyle} />
            </div>
            <button onClick={addMed}
              style={{ padding: '9px 14px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontWeight: 'bold' }}>
              + Add
            </button>
          </div>
        </div>

        {/* ── SECTION 4: General Notes ── */}
        <div style={{ marginBottom: '18px' }}>
          <label style={{ ...labelStyle, fontSize: '0.9rem' }}>📝 Notes</label>
          <textarea rows={3} placeholder="Koi aur notes..."
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            style={{ ...inputStyle, resize: 'vertical', minHeight: '72px' }} />
        </div>

        {/* Save at bottom too */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingBottom: '20px' }}>
          <button onClick={onCancel}
            style={{ padding: '10px 22px', borderRadius: '8px', border: '1px solid #dee2e6', background: 'white', cursor: 'pointer', color: '#555', fontWeight: 'bold' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '10px 30px', borderRadius: '8px', border: 'none', backgroundColor: '#006494', color: 'white', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '1rem' }}>
            {saving ? 'Saving...' : '💾 Save Parcha'}
          </button>
        </div>
      </div>

      {/* ── RIGHT: PRESET SIDE PANEL ── */}
      <div style={{ borderLeft: '2px solid #e9ecef', paddingLeft: '16px', position: 'sticky', top: 0, alignSelf: 'start', maxHeight: '80vh', overflowY: 'auto' }}>
        <p style={{ fontSize: '0.78rem', color: '#aaa', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 'bold' }}>
          Quick Presets
        </p>

        {/* Field selector tabs */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '14px' }}>
          {PRESET_FIELDS.map(f => (
            <button key={f.key} onClick={() => setActivePresetKey(f.key)}
              style={{ padding: '6px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '0.8rem', fontWeight: 'bold',
                backgroundColor: activePresetKey === f.key ? '#006494' : '#e9ecef',
                color: activePresetKey === f.key ? 'white' : '#555' }}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Preset items for active field */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {(presets[activePresetKey] || []).length === 0
            ? <p style={{ color: '#bbb', fontSize: '0.78rem', textAlign: 'center', marginTop: '20px' }}>
                Add options in Settings for this field
              </p>
            : (presets[activePresetKey] || []).map(item => (
              <button key={item._id}
                onClick={() => appendPreset(activePresetKey, item.label)}
                style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #dee2e6', backgroundColor: 'white', cursor: 'pointer', textAlign: 'left', fontSize: '0.82rem', color: '#333', transition: '0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#e8f4fd'; e.currentTarget.style.borderColor = '#006494'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.borderColor = '#dee2e6'; }}>
                + {item.label}
              </button>
            ))
          }
        </div>
      </div>

      {/* Override modal — forces a reason before saving past a severe block */}
      {showOverride && safety && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        }} onClick={e => { if (e.target === e.currentTarget) setShowOverride(false); }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '22px', width: '540px', maxHeight: '86vh', overflow: 'auto', border: '2px solid #dc3545' }}>
            <h3 style={{ margin: 0, color: '#c0392b', fontSize: '1.1rem' }}>⛔ Severe Drug Safety Issue</h3>
            <p style={{ fontSize: '0.85rem', color: '#555', margin: '8px 0 14px' }}>
              Veeransh ne {safety.severe.length} blocking issue{safety.severe.length > 1 ? 's' : ''} detect kiye hain. Agar aap save karna chahte ho, clinical justification likhna zaruri hai — yeh record audit trail me jaayega.
            </p>
            <div style={{ background: '#fff5f5', padding: '10px', borderRadius: '8px', marginBottom: '14px' }}>
              {safety.severe.map((s, i) => (
                <div key={i} style={{ fontSize: '0.82rem', padding: '6px 0', borderBottom: i < safety.severe.length - 1 ? '1px solid #ffc0c0' : 'none' }}>
                  <strong style={{ color: '#c0392b' }}>{s.drug}:</strong> {s.message}
                </div>
              ))}
            </div>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 'bold', color: '#555', marginBottom: '6px' }}>
              Clinical justification (mandatory):
            </label>
            <textarea value={overrideReason} onChange={e => setOverrideReason(e.target.value)} rows={4}
              placeholder="e.g. Patient tolerated this drug before without reaction. Alternative not available. Benefit outweighs risk in this case."
              style={{ ...inputStyle, resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '14px' }}>
              <button onClick={() => { setShowOverride(false); setOverrideReason(''); }}
                style={{ padding: '9px 18px', borderRadius: '7px', border: '1px solid #dee2e6', background: 'white', cursor: 'pointer', color: '#555', fontWeight: 'bold' }}>
                Cancel
              </button>
              <button onClick={confirmOverride} disabled={saving || !overrideReason.trim()}
                style={{ padding: '9px 18px', borderRadius: '7px', border: 'none', background: (saving || !overrideReason.trim()) ? '#f5a5a5' : '#dc3545', color: 'white', cursor: (saving || !overrideReason.trim()) ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}>
                {saving ? 'Saving…' : 'Override & Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
